import {IFormElement, IFormSection, TElementDictionaries} from '~/interfaces/formSubmissionEditInterfaces'
import {ArrayHelper} from '~/helpers/arrayHelper'
import Formula from '~/models/formula'
import {IDynamicIdComponents} from '~/interfaces/dynamicFieldsInterfaces'
import FormSubmissionConditionalRuleValue from '~/utils/formSubmissionConditionalRuleValue'
import FormElementDefinition from '~/definitions/formElementDefinition'

/**
 * @param identifier usually element client_id but occasionally the label_title/reference_name
 * @param formSection
 * @param callback
 */
export function formElementFinder(identifier: string, formSection: IFormSection, callback: (fe: IFormElement) => boolean = fe => fe.client_id === identifier): IFormElement {
  return ArrayHelper.find(formSection.form_elements, callback, { recurse: 'form_elements' })
}

/**
 * variable usually has the shape of +f_<client_id without dashes>+
 * @see {@link Formula.parseDynamicId}
 *
 * if id components can't be derived through +Formula.parseDynamicId+ initially,
 * then check if variable name matches a label_title/reference_name of a form element
 *   accounts for formulae like IF(Cat = 'Dog', 'Yay Heading', 'Not Cat') where +Cat+ is a variable name
 *
 * @param variableName
 * @param formSections where to look for form elements if not in +dictionaries+ already
 * @param dictionaries a dictionary per form section to quickly find form elements
 * @param prefix string to return in id component hash if hash must be created through finding element by reference_name/label_title
 */
export function toFormulaIdComponents({
                                        variableName,
                                        formSections,
                                        dictionaries,
                                        prefix,
                                      }: { variableName: string, formSections: readonly IFormSection[], dictionaries: TElementDictionaries, prefix: string }): IDynamicIdComponents {
  let idComponents = Formula.parseDynamicId(variableName)
  if (idComponents.id) return idComponents

  // match variable name to reference_name or label_title
  const matchFormulaName = (fe: IFormElement) => (fe.reference_name || fe.label_title) === variableName
  for (const formSection of formSections) {
    const condRuleValueFinder = new FormSubmissionConditionalRuleValue({
      formSection,
      elementFinder: (elIdentifier: string, fs: IFormSection) => {
        return formElementFinder(elIdentifier, fs, matchFormulaName)
      },
      elementDictionary: dictionaries[formSection.client_id],
    })
    const element = condRuleValueFinder.getElement(variableName)
    if (!element) continue

    return { prefix, id: Formula.clientIdToFormulaId(element.client_id), meta: null }
  }

  return null
}

export function label(fe: Pick<IFormElement, 'element_type' | 'label_title' | 'reference_name'>): string {
  return fe.label_title || fe.reference_name || `Untitled ${controlName(fe)}`
}

export function controlName(fe: Pick<IFormElement, 'element_type'>): string {
  return FormElementDefinition.getDisplayName(fe.element_type)
}
